/* eslint-disable no-param-reassign */
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning'
import { useT } from '@transifex/react'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { selectSession } from '../store'
import { debounce } from '../utils'

const beep = new Audio('https://iframe-assets.s3.eu-west-1.amazonaws.com/audios/beep.mp3')

export default function useBarcodeScanner(): [() => void, boolean, string] {
  const [isScanning, setIsScanning] = useState(false)
  const [barcode, setBarcode] = useState('')
  const { theme } = useSelector(selectSession)
  const t = useT()

  const finishScan = debounce((barcodeValue: string | null, bodyEl: HTMLElement, rootEl: HTMLElement, torchEl: HTMLElement, scannerEl: HTMLElement, buttonEl: HTMLElement, torchCleanup?: () => void) => {
    setIsScanning(false)
    BarcodeScanner.stopScan()
    BarcodeScanner.removeAllListeners()
    torchCleanup?.()

    if (barcodeValue) {
      beep.play()
      Haptics.impact({ style: ImpactStyle.Light })
      setBarcode(barcodeValue)
    }

    if (bodyEl) {
      bodyEl.style.background = theme === 'dark' ? '#000' : '#fff'
    }

    if (rootEl) {
      rootEl.style.opacity = '1'
      rootEl.style.background = theme === 'dark' ? '#000' : '#fff'
    }
    if (torchEl) {
      torchEl.style.opacity = '0'
    }
    if (scannerEl) {
      scannerEl.style.opacity = '0'
      scannerEl.style.zIndex = '-1'
    }
    if (buttonEl) {
      buttonEl.style.opacity = '0'
    }
  }, 500)

  const startScan = () => {
    setBarcode('')
    setIsScanning(true)

    if (Capacitor.isNativePlatform()) {
      const { body } = document
      const rootEl = document.getElementById('root') as HTMLElement
      const scannerEl = document.getElementsByClassName('scanner-ui')[0] as HTMLElement
      const backButtonEl = document.getElementById('scanner-back-button') as HTMLElement
      const torchButtonEl = document.getElementById('scanner-torch-button') as HTMLElement

      const torchButtonListener = () => {
        Haptics.impact({ style: ImpactStyle.Light })
        BarcodeScanner.toggleTorch()
      }

      const cleanupTorchListener = () => {
        torchButtonEl?.removeEventListener('click', torchButtonListener)
      }

      BarcodeScanner.requestPermissions()
        .then((result) => {
          if (result.camera === 'granted') {
            if (body) {
              body.style.background = 'transparent'
            }

            if (rootEl) {
              rootEl.style.opacity = '0'
              rootEl.style.background = 'transparent'
            }
            if (scannerEl) {
              scannerEl.style.opacity = '1'
              scannerEl.style.zIndex = '999'

              if (torchButtonEl) {
                torchButtonEl.style.opacity = '1'
                torchButtonEl.addEventListener('click', torchButtonListener)
              }

              const scannerTextEl = document.getElementById('scanner-text') as HTMLElement

              if (scannerTextEl) {
                scannerTextEl.textContent = t('Scanning...')
              }

              if (backButtonEl) {
                backButtonEl.textContent = t('Back')
                backButtonEl.style.opacity = '1'

                const backButtonListener = () => {
                  finishScan(null, body, rootEl, torchButtonEl, scannerEl, backButtonEl, cleanupTorchListener)
                }

                backButtonEl.removeEventListener('click', backButtonListener)
                backButtonEl.addEventListener('click', backButtonListener)
              }
            }

            BarcodeScanner.addListener('barcodesScanned', ({ barcodes }) => {
              if (barcodes.length === 0) return

              setIsScanning(false)

              finishScan(barcodes[0].displayValue || '', body, rootEl, torchButtonEl, scannerEl, backButtonEl, cleanupTorchListener)
            })

            BarcodeScanner.startScan()
          }
        })
        .catch((e) => {
          console.log('permission error', e)
        })
    }
  }

  return [startScan, isScanning, barcode]
}
